import { lazy } from 'react';

import EventBusInstance from '@core/eventbus';
import LivePolling from '@live/pollingHandler';
import { getAdvertising, loadJwPlayer, trackedVideoKinds } from '@services/JwPlayerService';
import { injectReact } from '@shared/render-utils';
import type { Draw } from '@shared-types/draw.types';
import { MatchStatus } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import User from '@user/User';
import { IS_SIMULATION, IS_DEBUG } from '@utilities/browser';
import { formatUrl } from '@utilities/fetch';
import { Trace } from '@utilities/logging';
import { getUniqueId } from '@utilities/random';
import { drawApiUrl, apiKey } from '@utilities/WindowContext';

export type DrawVideoPluginOptions = {
  drawId?: string;
  interval?: number;
  skin?: string;
  activecolor?: string;
  poster?: string;
  autostart?: boolean;
  activateLogin?: boolean;
  videoTitle?: string;
  videoType?: string;
  sharing?: boolean;
  sharingurl?: string;
  sharingtxt?: string;
};

// Default configuration properties.
const defaults: DrawVideoPluginOptions = {
  interval: 10000,
  skin: '',
  activecolor: '',
};

export default class DrawVideoPlugin {
  dom: HTMLElement;
  opt: DrawVideoPluginOptions;
  targetId: string;
  contentWrapper: HTMLElement;
  videoWrapper: HTMLElement;
  wrapper: HTMLElement;
  poster: HTMLElement;
  play: HTMLElement;
  jwInstance: jwplayer.JWPlayer;
  src: string;
  playerInited: boolean;
  locked: boolean;
  hasRetried: boolean;
  colsContainer: HTMLElement;
  endPoint: string;

  constructor(dom: HTMLElement, o: DrawVideoPluginOptions) {
    this.dom = dom;
    this.opt = {
      ...defaults,
      ...o,
    };

    this.targetId = getUniqueId('drawvideo_');
    this.endPoint = formatUrl(drawApiUrl, 'v1/draws', {
      drawId: this.opt.drawId,
      optionalFields: ['TEAMS'],
    });
  }

  get lockPanel() {
    return (
      this.dom.querySelector<HTMLElement>('.js-video-lock') ?? document.querySelector<HTMLElement>('.js-video-lock')
    );
  }

  init() {
    this.colsContainer = document.querySelector<HTMLElement>('.draw-content-wrapper .pk-col--content');
    this.videoWrapper = document.querySelector<HTMLElement>('.draw-header__video');
    this.contentWrapper = this.dom.querySelector<HTMLElement>('.js-drawvideo-content-wrapper');
    this.wrapper = this.dom.querySelector<HTMLElement>('.js-drawvideo-wrapper') ?? null;
    this.poster = this.dom.querySelector<HTMLElement>('.jw-poster');
    this.play = this.dom.querySelector<HTMLElement>('.icon-play');
    this.wrapper?.setAttribute('id', this.targetId);

    LivePolling.on(this.endPoint, [{ key: 'x-api-key', value: apiKey }], this.opt.interval, this.checkVideo, this);
  }

  checkVideo(draws: Draw[]) {
    if (!draws) return;
    const data = draws[0];

    if (
      data &&
      (data.status.videoStreamAvailable || IS_SIMULATION || data.status.eventStatus === MatchStatus.FINISHED) &&
      data.videoPath?.length
    ) {
      this.src = data.videoPath;
      this.checkLock();
      EventBusInstance.on('userLogged', this.checkLock, this);
      this.videoWrapper?.classList.remove('hidden');
      this.colsContainer?.classList.remove('no-video-available');
    } else {
      this.src = null;
      this.resetPlayer();
      this.videoWrapper?.classList.add('hidden');
      this.colsContainer?.classList.add('no-video-available');
    }
  }

  resetPlayer() {
    if (!this.playerInited) return;

    if (this.jwInstance) {
      this.jwInstance.remove();
    }
    this.playerInited = false;
  }

  get lockStatus() {
    if (!this.lockPanel) return true;

    if (this.opt.activateLogin) return User.logged;

    return true;
  }

  checkLock() {
    // avoid multiple init
    if (this.locked === this.lockStatus) return;
    this.locked = this.lockStatus;

    if (this.lockStatus) this.initPlayer();
    else this.initLockpanel();
  }

  initLockpanel() {
    this.contentWrapper.classList.remove('hidden');
    this.poster.classList.remove('hidden');
    this.play.classList.remove('hidden');
    if (this.jwInstance && this.playerInited) this.jwInstance.remove();

    const VideoLock = lazy(() => import(/* webpackPrefetch: true */ '@shared/VideoLock'));
    injectReact(VideoLock, { ...this.opt }, this.lockPanel);
  }

  get videoType() {
    const kind = this.opt.videoType?.toLowerCase();
    return trackedVideoKinds.includes(kind) ? kind : 'video';
  }

  async initPlayer() {
    this.poster?.classList.add('hidden');
    this.play?.classList.add('hidden');

    if (this.playerInited) return;
    this.playerInited = true;

    this.contentWrapper?.classList.remove('hidden');

    const jwOptions: any = {
      file: this.src,
      image: this.opt.poster,
      debug: IS_DEBUG,
      width: '100%',
      hlshtml: true,
      primary: 'html5',
      autostart: this.opt.autostart,
      autoPause: false,
      mute: true,
      controls: true,
      displaytitle: false,
      title: this.opt.videoTitle,
      videoType: this.videoType,
      displaydescription: false,
      repeat: false,
      androidhls: true,
      aspectratio: '16:9',
      doNotSaveCookies: true,
      skin: {
        name: this.opt.skin,
        active: this.opt.activecolor.length ? this.opt.activecolor : null,
      },
      advertising: getAdvertising(),
      events: {
        onReady: () => {
          GoogleTagManagerHelper._push({ event: 'floatingPlayerLoaded' });

          // Move the timeslider in-line with other controls
          const playerContainer = this.jwInstance.getContainer();
          const buttonContainer = playerContainer.querySelector('.jw-button-container');
          const timeSlider = playerContainer.querySelector('.jw-slider-time');
          const spacer = buttonContainer.querySelector('.jw-spacer');
          buttonContainer.classList.add('jw-background-color');
          buttonContainer.querySelector('.jw-icon-volume .jw-overlay')?.classList.add('jw-background-color');
          spacer.replaceWith(timeSlider);

          if (this.jwInstance.getState() !== 'playing') this.jwInstance.play(true);
        },
        onError: (e) => {
          Trace('error', e);
          if (!this.hasRetried) {
            this.hasRetried = true;
            this.jwInstance.stop();
            this.jwInstance.play();
          }
        },
        onFullscreen: (e: jwplayer.FullscreenParam) => {
          if (e.fullscreen)
            GoogleTagManagerHelper.event(
              'Videos',
              'Fullscreen',
              this.opt.videoTitle,
              { videoType: this.opt.videoType.toLowerCase() ?? 'draw live', videoTitle: this.opt.videoTitle },
              null,
              false,
              'Video Event',
            );
        },
      },
    };

    if (this.opt.sharing) {
      jwOptions.sharing = {
        link: this.opt.sharingurl,
        heading: this.opt.sharingtxt,
        sites: ['facebook', 'twitter'],
      };
    }

    const jwplayer = await loadJwPlayer();
    this.jwInstance = jwplayer(this.targetId).setup(jwOptions);
  }

  detach() {
    EventBusInstance.off('userLogged', this.checkLock, this);
    LivePolling.off(this.endPoint, this.checkVideo, this);
  }
}
